"use client";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "@/common/Modal";
import Link from "next/link";
import Like from "@/components/Svg/Like";
import User from "@/components/Svg/User";
import BasketIcon from "@/components/Svg/BasketIcon";
import CardBasket from "@/components/basket/CardBasket";
import CloseBasketIcon from "@/components/Svg/CloseBasketIcon";
import useCartStore from "@/store/store";
import Image from "next/image";
import { useModal } from "@/hooks/useModal";
import Cookies from "js-cookie";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import UserDropDown from "./UserDropDown";
import useSoftSetting from "@/hooks/useSoftSetting";

function LeftSectionTopbar() {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { data } = useSoftSetting();
  useEffect(() => {
    const token = Cookies.get("AccessToken");
    setAccessToken(token || null);
  }, []);
  const flexItems = "flex items-center";
  const { isOpen, openModal, closeModal } = useModal();
  const router = useRouter();

  const { productCard, loadCard } = useCartStore();
  useEffect(() => {
    loadCard();
  }, [loadCard]);

  const submitOrder = useCallback(
    async (event?: React.FormEvent) => {
      if (event) {
        event.preventDefault();
      }
      if (!accessToken) {
        toast.warning("لطفا وارد حساب کاربری خود شوید");
        const loginUrl = "/login?redirect=/basket";
        router.push(loginUrl);
      } else {
        router.push("/basket");
        closeModal();
      }
    },
    [accessToken, router]
  );

  const [localStorageData, setLocalStorageData] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      loadCard();
      updateLocalStorageData();
    }
  }, [isMounted, loadCard]);
  const calculateTotalPrice = useCallback(() => {
    if (!isMounted) return 0;
    return (
      productCard?.reduce((total, product) => {
        return total + product.originalPrice * (product.quantity || 1);
      }, 0) || 0
    );
  }, [isMounted, productCard]);
  const updateLocalStorageData = () => {
    const storedData = window.localStorage.getItem("productCard");
    if (storedData) {
      try {
        setLocalStorageData(JSON.parse(storedData));
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
        setLocalStorageData([]);
      }
    } else {
      setLocalStorageData([]);
    }
  };
  useEffect(() => {
    if (!isMounted) return;
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "productCard") {
        updateLocalStorageData();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const interval = setInterval(updateLocalStorageData, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [isMounted]);
  return (
    <div className={`${flexItems} gap-5`}>
      <Link
        href="/dashboard/liked"
        className="like-icon hidden sm:flex cursor-pointer"
      >
        <Like className="w-5" />
      </Link>
      {!accessToken ? (
        <Link
          href="/dashboard"
          className="user-icon cursor-pointer hidden sm:flex"
        >
          <User />
        </Link>
      ) : (
        <UserDropDown />
      )}
      <span
        onClick={openModal}
        className="basket-icon cursor-pointer relative z-20"
      >
        <BasketIcon className="z-20" />
        <span className="text-[10px] num-vazir bg-gray-800 text-white absolute bottom-2 left-2 z-10 w-4.5 h-4.5 flex items-center justify-center rounded-full">
          {localStorageData?.length}
        </span>
      </span>
      {isOpen && (
        <Modal
          backdrop={true}
          backdropClosable
          onCloseHandler={closeModal}
          isOpen={isOpen}
          size="small"
          position="leftFull"
          modalHeaderClass="border-b-0 bg-ws-gray-200 header-basket px-10 py-6"
          modalTitleClass="flex justify-between w-full"
          modalCustomClass="top-4 sidebar-basket"
          confirmText="تکمیل سفارش"
          isValid={localStorageData?.length ? true : false}
          confirmBtnModalClass="w-full bg-ws-grayBlue-500 hover:bg-ws-grayBlue-600 transition hover:transition duration-300 mx-5 text-ws-white-100 rounded-lg py-7 text-xl"
          modalBodyClass="px-4 sm:px-8 pb-8 h-screen"
          enterFrom="left"
          onSubmit={submitOrder}
          modalDynamicBtnTitle={
            <span className="flex justify-between pt-5 px-8 mb-3">
              <span className="text-ws-gray-600 font-bold">مجموع</span>
              <span className="flex items-center gap-1">
                <span className="num-vazir text-ws-gray-600 font-bold">
                  {calculateTotalPrice()?.toLocaleString() ?? 0}
                </span>
                <span>{data?.currencyUnit}</span>
              </span>
            </span>
          }
          modalTitle={
            <>
              <div className="flex items-center w-full justify-between">
                <h2 className="text-base lg:text-2xl text-ws-gray-700">
                  سبد خرید
                </h2>
                <CloseBasketIcon
                  className="cursor-pointer w-4 h-4"
                  onClick={closeModal}
                />
              </div>
            </>
          }
        >
          {productCard?.length ? (
            <div className="flex flex-col">
              {productCard?.map((product, index) => (
                <CardBasket
                  hasMax
                  hasCounter
                  key={index + 1}
                  product={product}
                />
              ))}
            </div>
          ) : (
            <div className="w-full h-full flex justify-center items-center">
              <div className="flex flex-col gap-3 justify-center items-center mb-10">
                <Image
                  priority
                  fetchPriority="high"
                  sizes="100vw"
                  width={10000}
                  height={3000}
                  src="/images/empty-basket.png"
                  alt="image-for-empty basket"
                  className="sm:w-80 w-64"
                />
                <p className="text-ws-gray-600 sm:text-base text-xs">
                  سبد خرید شما خالیه!
                </p>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

export default LeftSectionTopbar;
